import { jsPDF } from 'jspdf';
import { ref, getBlob } from 'firebase/storage';
import { storage } from '../services/firebase';

export interface CertificateData {
  studentName: string;
  programName: string;
  date: string;
  schoolName: string;
  templateUrl?: string; // URL of the template background image
  directorName: string;
  directorTitle: string;
  trainerName: string;
  trainerTitle: string;
}

/**
 * Loads an image from Firebase Storage URL and returns base64 data
 * Uses Firebase SDK to handle CORS properly
 */
export const loadImageAsBase64 = async (url: string): Promise<string> => {
  try {
    console.log('Loading image from Firebase Storage:', url);

    // Extract the storage path from the URL
    // URL format: https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{encodedPath}?alt=media&token={token}
    const match = url.match(/\/o\/([^?]+)/);
    if (!match) {
      throw new Error('Invalid Firebase Storage URL');
    }
    const storagePath = decodeURIComponent(match[1]);
    console.log('Storage path:', storagePath);

    // Use Firebase SDK to get the blob
    const storageRef = ref(storage, storagePath);
    const blob = await getBlob(storageRef);
    console.log('Image blob received via Firebase SDK, size:', blob.size, 'type:', blob.type);

    // Convert blob to base64
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        console.log('Image converted to base64, length:', base64.length);
        resolve(base64);
      };
      reader.onerror = () => {
        reject(new Error('Failed to read blob as base64'));
      };
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.error('Failed to load image from Firebase Storage:', url, e);
    throw e;
  }
};

export type OverlayMode = 'studentNameOnly' | 'allFields';

/**
 * Generates a Certificate PDF using template image as background
 * @param overlayMode - 'studentNameOnly' overlays just the name, 'allFields' overlays all dynamic content
 */
export const generateParticipationCertificate = async (
  data: CertificateData,
  templateBase64?: string,
  overlayMode: OverlayMode = 'studentNameOnly'
): Promise<jsPDF> => {
  // A4 Portrait orientation
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // If template image is provided, use it as full-page background
  if (templateBase64) {
    try {
      // Detect image format from base64 header
      const format = templateBase64.includes('image/png') ? 'PNG' : 'JPEG';
      console.log('Adding template image, format:', format);
      // Add template as full-page background image
      doc.addImage(templateBase64, format, 0, 0, pageWidth, pageHeight);
      console.log('Template image added successfully');
    } catch (e) {
      console.error('Failed to add template background:', e);
      // Fall back to generating without template
      drawFallbackCertificate(doc, data, pageWidth, pageHeight);
      return doc;
    }
  } else {
    console.log('No template provided, using fallback');
    // No template provided, draw fallback certificate
    drawFallbackCertificate(doc, data, pageWidth, pageHeight);
    return doc;
  }

  const darkTextColor: [number, number, number] = [33, 33, 33];
  const grayTextColor: [number, number, number] = [100, 100, 100];

  if (overlayMode === 'studentNameOnly') {
    // Overlay only the student name on the template
    // All other text (program name, date, signatures) comes from the template image
    doc.setTextColor(...darkTextColor);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text(data.studentName, pageWidth / 2, 127, { align: 'center' });
  } else {
    // Overlay all dynamic fields on the template

    // Student Name
    doc.setTextColor(...darkTextColor);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text(data.studentName, pageWidth / 2, 127, { align: 'center' });

    // Program Name
    doc.setTextColor(...darkTextColor);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    const programLines = doc.splitTextToSize(data.programName, pageWidth - 60);
    let programY = 155;
    programLines.forEach((line: string) => {
      doc.text(line, pageWidth / 2, programY, { align: 'center' });
      programY += 7;
    });

    // Date
    doc.setTextColor(...grayTextColor);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(data.date, pageWidth / 2, programY + 15, { align: 'center' });

    // Signature section
    const signatureY = pageHeight - 55;
    const signatureWidth = 50;

    // Left signature (Director)
    doc.setTextColor(...darkTextColor);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(data.directorName, 35 + signatureWidth / 2, signatureY + 6, { align: 'center' });
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(data.directorTitle, 35 + signatureWidth / 2, signatureY + 12, { align: 'center' });

    // Right signature (Trainer)
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(data.trainerName, pageWidth - 35 - signatureWidth / 2, signatureY + 6, { align: 'center' });
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(data.trainerTitle, pageWidth - 35 - signatureWidth / 2, signatureY + 12, { align: 'center' });
  }

  return doc;
};

/**
 * Draws a fallback certificate when no template is provided
 */
function drawFallbackCertificate(
  doc: jsPDF,
  data: CertificateData,
  pageWidth: number,
  pageHeight: number
) {
  const goldColor: [number, number, number] = [139, 119, 42];
  const darkTextColor: [number, number, number] = [33, 33, 33];
  const grayTextColor: [number, number, number] = [100, 100, 100];
  const sealColor: [number, number, number] = [180, 40, 40];

  // Background - cream/off-white
  doc.setFillColor(255, 252, 240);
  doc.rect(0, 0, pageWidth, pageHeight, 'F');

  // Outer border (gold)
  doc.setDrawColor(...goldColor);
  doc.setLineWidth(2);
  doc.rect(10, 10, pageWidth - 20, pageHeight - 20);

  // Inner border (gold, thinner)
  doc.setLineWidth(0.5);
  doc.rect(15, 15, pageWidth - 30, pageHeight - 30);

  // Corner decorations
  drawCornerDecoration(doc, 12, 12, goldColor, 'top-left');
  drawCornerDecoration(doc, pageWidth - 12, 12, goldColor, 'top-right');
  drawCornerDecoration(doc, 12, pageHeight - 12, goldColor, 'bottom-left');
  drawCornerDecoration(doc, pageWidth - 12, pageHeight - 12, goldColor, 'bottom-right');

  // "Certificate of Participation"
  doc.setTextColor(...darkTextColor);
  doc.setFontSize(42);
  doc.setFont('times', 'italic');
  doc.text('Certificate of Participation', pageWidth / 2, 70, { align: 'center' });

  // "This certificate is proudly awarded to"
  doc.setTextColor(...grayTextColor);
  doc.setFontSize(14);
  doc.setFont('times', 'italic');
  doc.text('This certificate is proudly awarded to', pageWidth / 2, 95, { align: 'center' });

  // Student Name
  doc.setTextColor(...darkTextColor);
  doc.setFontSize(28);
  doc.setFont('helvetica', 'bold');
  doc.text(data.studentName, pageWidth / 2, 115, { align: 'center' });

  // Underline for student name
  const nameWidth = doc.getTextWidth(data.studentName);
  doc.setDrawColor(...goldColor);
  doc.setLineWidth(0.5);
  doc.line(pageWidth / 2 - nameWidth / 2 - 10, 118, pageWidth / 2 + nameWidth / 2 + 10, 118);

  // "in recognition of his/her participation in the"
  doc.setTextColor(...grayTextColor);
  doc.setFontSize(12);
  doc.setFont('times', 'italic');
  doc.text('in recognition of his/her participation in the', pageWidth / 2, 133, { align: 'center' });

  // Program Name
  doc.setTextColor(...darkTextColor);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  const programLines = doc.splitTextToSize(data.programName, pageWidth - 60);
  let programY = 150;
  programLines.forEach((line: string) => {
    doc.text(line, pageWidth / 2, programY, { align: 'center' });
    programY += 8;
  });

  // "held on"
  doc.setTextColor(...grayTextColor);
  doc.setFontSize(12);
  doc.setFont('times', 'italic');
  doc.text('held on', pageWidth / 2, programY + 10, { align: 'center' });

  // Date
  doc.setTextColor(...darkTextColor);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(data.date, pageWidth / 2, programY + 25, { align: 'center' });

  // Signature section
  const signatureY = pageHeight - 60;
  const signatureWidth = 50;

  // Left signature (Director)
  doc.setDrawColor(...darkTextColor);
  doc.setLineWidth(0.3);
  doc.line(35, signatureY, 35 + signatureWidth, signatureY);
  doc.setTextColor(...darkTextColor);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(data.directorName, 35 + signatureWidth / 2, signatureY + 6, { align: 'center' });
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(data.directorTitle, 35 + signatureWidth / 2, signatureY + 12, { align: 'center' });

  // Center seal
  drawSeal(doc, pageWidth / 2, signatureY - 5, 15, sealColor);

  // Right signature (Trainer)
  doc.line(pageWidth - 35 - signatureWidth, signatureY, pageWidth - 35, signatureY);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(data.trainerName, pageWidth - 35 - signatureWidth / 2, signatureY + 6, { align: 'center' });
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(data.trainerTitle, pageWidth - 35 - signatureWidth / 2, signatureY + 12, { align: 'center' });
}

/**
 * Draws simplified corner decorations
 */
function drawCornerDecoration(
  doc: jsPDF,
  x: number,
  y: number,
  color: [number, number, number],
  position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
) {
  doc.setDrawColor(...color);
  doc.setLineWidth(1);

  const size = 20;
  const curl = 8;

  switch (position) {
    case 'top-left':
      doc.line(x, y + 5, x + size, y + 5);
      doc.line(x + size, y + 5, x + size + curl, y + 2);
      doc.line(x + 5, y, x + 5, y + size);
      doc.line(x + 5, y + size, x + 2, y + size + curl);
      break;
    case 'top-right':
      doc.line(x, y + 5, x - size, y + 5);
      doc.line(x - size, y + 5, x - size - curl, y + 2);
      doc.line(x - 5, y, x - 5, y + size);
      doc.line(x - 5, y + size, x - 2, y + size + curl);
      break;
    case 'bottom-left':
      doc.line(x, y - 5, x + size, y - 5);
      doc.line(x + size, y - 5, x + size + curl, y - 2);
      doc.line(x + 5, y, x + 5, y - size);
      doc.line(x + 5, y - size, x + 2, y - size - curl);
      break;
    case 'bottom-right':
      doc.line(x, y - 5, x - size, y - 5);
      doc.line(x - size, y - 5, x - size - curl, y - 2);
      doc.line(x - 5, y, x - 5, y - size);
      doc.line(x - 5, y - size, x - 2, y - size - curl);
      break;
  }
}

/**
 * Draws a stylized wax seal
 */
function drawSeal(
  doc: jsPDF,
  x: number,
  y: number,
  radius: number,
  color: [number, number, number]
) {
  doc.setFillColor(...color);

  const points = 24;
  const innerRadius = radius * 0.7;

  for (let i = 0; i < points; i++) {
    const angle1 = (i * 2 * Math.PI) / points;
    const angle2 = ((i + 1) * 2 * Math.PI) / points;

    const r1 = i % 2 === 0 ? radius : innerRadius;
    const r2 = (i + 1) % 2 === 0 ? radius : innerRadius;

    const x1 = x + r1 * Math.cos(angle1);
    const y1 = y + r1 * Math.sin(angle1);
    const x2 = x + r2 * Math.cos(angle2);
    const y2 = y + r2 * Math.sin(angle2);

    doc.triangle(x, y, x1, y1, x2, y2, 'F');
  }

  doc.setFillColor(color[0] - 30, color[1] - 10, color[2] - 10);
  doc.circle(x, y, innerRadius * 0.7, 'F');
}

/**
 * Download the certificate as PDF
 */
export const downloadCertificate = async (data: CertificateData, filename?: string) => {
  let templateBase64: string | undefined;
  console.log('downloadCertificate called, templateUrl:', data.templateUrl);
  if (data.templateUrl) {
    try {
      templateBase64 = await loadImageAsBase64(data.templateUrl);
      console.log('Template loaded, base64 length:', templateBase64?.length);
    } catch (e) {
      console.error('Failed to load template:', e);
    }
  }
  const doc = await generateParticipationCertificate(data, templateBase64);
  const name = filename || `Certificate_${data.studentName.replace(/\s+/g, '_')}.pdf`;
  doc.save(name);
};

/**
 * Open certificate in new tab for preview
 */
export const previewCertificate = async (data: CertificateData) => {
  let templateBase64: string | undefined;
  console.log('previewCertificate called, templateUrl:', data.templateUrl);
  if (data.templateUrl) {
    try {
      templateBase64 = await loadImageAsBase64(data.templateUrl);
      console.log('Template loaded for preview, base64 length:', templateBase64?.length);
    } catch (e) {
      console.error('Failed to load template:', e);
    }
  }
  const doc = await generateParticipationCertificate(data, templateBase64);
  const pdfBlob = doc.output('blob');
  const url = URL.createObjectURL(pdfBlob);
  window.open(url, '_blank');
};
